import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import ToggleSwitch from '../WidgetEditComponents/ToggleSwitch';
import SliderSection from '../WidgetEditComponents/SliderSection';
import LayoutSelector from '../WidgetEditComponents/LayoutSelector';
import ButtonWidgetStyle from '../WidgetEditComponents/ButtonWidgetStyle';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function HiWidgetEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // Image Upload States
    const [isUploadingProfile, setIsUploadingProfile] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Widget-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.layoutType);

    // Layout Change Handler
    const handleLayoutChange = (layoutId, layoutConfig) => {
        const fakeEvent = {
            target: {
                name: 'layoutType',
                value: layoutId,
                type: 'text'
            }
        };
        handleInputChange(fakeEvent);
    };

    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 HiWidgetEdit initialized - layout variations now active');
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.layoutType;
        
        // ✅ CRITICAL: Only apply layout variations on ACTUAL layout changes, not widget switches
        if (!isInitialized) {
            console.log('🚫 HiWidgetEdit: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 HiWidgetEdit: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 HiWidgetEdit REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS
        const layoutVariations = {
            'classic-profile': {
                height: 'auto',     // ✅ AUTO HEIGHT ALS STANDARD
                heightMobile: 'auto', // ✅ AUTO HEIGHT MOBILE ALS STANDARD
                paddingTop: 80,     // ✅ REDUZIERTES PADDING
                paddingTopMobile: 40,
                paddingBottom: 80,  // ✅ REDUZIERTES PADDING
                paddingBottomMobile: 40,
                profileImageSize: 200, // ✅ ORDENTLICHE STANDARD-GRÖSSE
                profileImageBorder: false, // ✅ KEIN FRAME ALS STANDARD
                profileImageBorderWidth: 0, // ✅ KEIN BORDER
                profileImageShadow: false, // ✅ KEIN SHADOW ALS STANDARD
                contentSpacing: 40,
                contentSpacingMobile: 20,
                // ✅ NEUE LAYOUT CONTROLS
                textMaxWidth: 600,
                textMaxWidthMobile: 400,
                imageTextGap: 40,
                imageTextGapMobile: 20,
                textVerticalAlign: 'center',
                desktopFlipSides: false,
                mobileFlipSides: false,
                mobileStackOrder: 'image-first',
                elementSpacing: 20,
                elementSpacingMobile: 15,
                textLineHeight: 1.6,
                // ✅ FULL-HEIGHT IMAGE WIDTH DEFAULTS
                fullHeightImageWidth: 35,
                fullHeightImageWidthMobile: 40,
                // ✅ IMAGE MARGIN TO EDGE (Side-by-Side Layout)
                imageMarginToEdge: 40
            },
            'image-focus': {
                height: 'auto',     // ✅ AUTO HEIGHT
                heightMobile: 'auto', // ✅ AUTO HEIGHT MOBILE
                paddingTop: 80,     // ✅ REDUZIERTES PADDING
                paddingTopMobile: 40,
                paddingBottom: 80,  // ✅ REDUZIERTES PADDING
                paddingBottomMobile: 40,
                profileImageSize: 250, // ✅ ORDENTLICHE GRÖSSE (nicht zu groß)
                profileImageBorder: false, // ✅ KEIN FRAME
                profileImageBorderWidth: 0,
                profileImageShadow: false, // ✅ KEIN SHADOW
                contentSpacing: 50,
                contentSpacingMobile: 25,
                // ✅ IMAGE-FOCUS SPEZIFISCHE CONTROLS
                textMaxWidth: 500,  // Schmaler für Image-Focus
                textMaxWidthMobile: 350,
                imageTextGap: 50,   // Größerer Gap für Image-Focus
                imageTextGapMobile: 25,
                textVerticalAlign: 'center',
                desktopFlipSides: false,
                mobileFlipSides: false,
                mobileStackOrder: 'image-first',
                elementSpacing: 25, // Größerer Spacing für Image-Focus
                elementSpacingMobile: 20,
                textLineHeight: 1.7,
                // ✅ FULL-HEIGHT IMAGE WIDTH DEFAULTS
                fullHeightImageWidth: 35,
                fullHeightImageWidthMobile: 40,
                // ✅ IMAGE MARGIN TO EDGE (Side-by-Side Layout)
                imageMarginToEdge: 50  // Image-Focus: Etwas mehr Margin
            },
            'centered-profile': {
                height: 'auto',     // ✅ AUTO HEIGHT
                heightMobile: 'auto', // ✅ AUTO HEIGHT MOBILE
                paddingTop: 80,
                paddingTopMobile: 40,
                paddingBottom: 80,
                paddingBottomMobile: 40,
                profileImageSize: 220, // ✅ ORDENTLICHE GRÖSSE für zentriert
                profileImageBorder: false, // ✅ KEIN FRAME
                profileImageBorderWidth: 0,
                profileImageShadow: false, // ✅ KEIN SHADOW
                contentSpacing: 40,
                contentSpacingMobile: 20,
                // ✅ CENTERED-PROFILE SPEZIFISCHE CONTROLS
                textMaxWidth: 700,  // Breiter für zentrierten Text
                textMaxWidthMobile: 450,
                imageTextGap: 30,   // Kleinerer Gap für vertikales Layout
                imageTextGapMobile: 20,
                textVerticalAlign: 'center',
                desktopFlipSides: false,
                mobileFlipSides: false,
                mobileStackOrder: 'image-first',
                elementSpacing: 25,
                elementSpacingMobile: 20,
                textLineHeight: 1.7,
                // ✅ FULL-HEIGHT IMAGE WIDTH DEFAULTS
                fullHeightImageWidth: 35,
                fullHeightImageWidthMobile: 40
            },
            'reverse-profile': {
                height: 'auto',     // ✅ AUTO HEIGHT
                heightMobile: 'auto', // ✅ AUTO HEIGHT MOBILE
                paddingTop: 80,     // ✅ REDUZIERTES PADDING
                paddingTopMobile: 40,
                paddingBottom: 80,  // ✅ REDUZIERTES PADDING
                paddingBottomMobile: 40,
                profileImageSize: 200, // ✅ ORDENTLICHE STANDARD-GRÖSSE
                profileImageBorder: false, // ✅ KEIN FRAME
                profileImageBorderWidth: 0,
                profileImageShadow: false, // ✅ KEIN SHADOW
                contentSpacing: 40,
                contentSpacingMobile: 20,
                // ✅ REVERSE-PROFILE SPEZIFISCHE CONTROLS
                textMaxWidth: 600,
                textMaxWidthMobile: 400,
                imageTextGap: 40,
                imageTextGapMobile: 20,
                textVerticalAlign: 'center',
                desktopFlipSides: false, // ✅ KEIN FLIP - Layout-Direction ist bereits row-reverse
                mobileFlipSides: false,
                mobileStackOrder: 'image-first',
                elementSpacing: 20,
                elementSpacingMobile: 15,
                textLineHeight: 1.6,
                // ✅ FULL-HEIGHT IMAGE WIDTH DEFAULTS
                fullHeightImageWidth: 35,
                fullHeightImageWidthMobile: 40,
                // ✅ IMAGE MARGIN TO EDGE (Side-by-Side Layout)
                imageMarginToEdge: 40
            },
            'text-only-center': {
                height: 'auto',     // ✅ AUTO HEIGHT
                heightMobile: 'auto', // ✅ AUTO HEIGHT MOBILE
                paddingTop: 80,
                paddingTopMobile: 40,
                paddingBottom: 80,
                paddingBottomMobile: 40,
                contentSpacing: 40,
                contentSpacingMobile: 20,
                // ✅ TEXT-ONLY SPEZIFISCHE CONTROLS
                textMaxWidth: 800,  // Maximale Breite für Text-Only
                textMaxWidthMobile: 500,
                imageTextGap: 0,    // Kein Gap da kein Image
                imageTextGapMobile: 0,
                textVerticalAlign: 'center',
                desktopFlipSides: false,
                mobileFlipSides: false,
                mobileStackOrder: 'text-first', // Text zuerst bei Text-Only
                elementSpacing: 30, // Größerer Spacing für Text-Only
                elementSpacingMobile: 25,
                textLineHeight: 1.8, // Bessere Lesbarkeit für Text-Only
                // ✅ FULL-HEIGHT IMAGE WIDTH DEFAULTS
                fullHeightImageWidth: 35,
                fullHeightImageWidthMobile: 40
            },
            'full-height-left': {
                height: 600,          // ✅ FESTE HÖHE für Full-Height Images (Desktop)
                heightMobile: 'auto', // ✅ AUTO HEIGHT MOBILE für responsive Verhalten
                paddingTop: 0,        // ✅ KEIN PADDING TOP für Full-Height
                paddingTopMobile: 40, // ✅ Mobile behält Padding
                paddingBottom: 0,     // ✅ KEIN PADDING BOTTOM für Full-Height
                paddingBottomMobile: 40, // ✅ Mobile behält Padding
                paddingLeft: 0,       // ✅ KEIN PADDING LEFT für Full-Height
                paddingLeftMobile: 20, // ✅ Mobile behält Padding
                paddingRight: 0,      // ✅ KEIN PADDING RIGHT für Full-Height
                paddingRightMobile: 20, // ✅ Mobile behält Padding
                marginTop: 0,         // ✅ KEIN MARGIN TOP
                marginBottom: 0,      // ✅ KEIN MARGIN BOTTOM
                marginLeft: 0,        // ✅ KEIN MARGIN LEFT
                marginRight: 0,       // ✅ KEIN MARGIN RIGHT
                borderRadius: 0,      // ✅ KEIN BORDER RADIUS für Full-Height
                profileImageSize: 300, // ✅ GRÖSSERE IMAGE SIZE für Full-Height
                profileImageBorder: false, // ✅ KEIN FRAME
                profileImageBorderWidth: 0,
                profileImageShadow: false, // ✅ KEIN SHADOW
                contentSpacing: 0,    // ✅ KEIN CONTENT SPACING für Full-Height
                contentSpacingMobile: 20, // ✅ Mobile behält Spacing
                // ✅ FULL-HEIGHT-LEFT SPEZIFISCHE CONTROLS
                textMaxWidth: 550,  // Angepasst für Full-Height Layout
                textMaxWidthMobile: 400,
                imageTextGap: 0,      // ✅ KEIN GAP für Full-Height - Image klebt am Rand
                imageTextGapMobile: 20, // Mobile hat Gap
                textVerticalAlign: 'center',
                desktopFlipSides: false,
                mobileFlipSides: false,
                mobileStackOrder: 'image-first',
                elementSpacing: 25,
                elementSpacingMobile: 20,
                textLineHeight: 1.6,
                // ✅ FULL-HEIGHT IMAGE WIDTH DEFAULTS
                fullHeightImageWidth: 35,
                fullHeightImageWidthMobile: 40
            },
            'full-height-right': {
                height: 600,          // ✅ FESTE HÖHE für Full-Height Images (Desktop)
                heightMobile: 'auto', // ✅ AUTO HEIGHT MOBILE für responsive Verhalten
                paddingTop: 0,        // ✅ KEIN PADDING TOP für Full-Height
                paddingTopMobile: 40, // ✅ Mobile behält Padding
                paddingBottom: 0,     // ✅ KEIN PADDING BOTTOM für Full-Height
                paddingBottomMobile: 40, // ✅ Mobile behält Padding
                paddingLeft: 0,       // ✅ KEIN PADDING LEFT für Full-Height
                paddingLeftMobile: 20, // ✅ Mobile behält Padding
                paddingRight: 0,      // ✅ KEIN PADDING RIGHT für Full-Height
                paddingRightMobile: 20, // ✅ Mobile behält Padding
                marginTop: 0,         // ✅ KEIN MARGIN TOP
                marginBottom: 0,      // ✅ KEIN MARGIN BOTTOM
                marginLeft: 0,        // ✅ KEIN MARGIN LEFT
                marginRight: 0,       // ✅ KEIN MARGIN RIGHT
                borderRadius: 0,      // ✅ KEIN BORDER RADIUS für Full-Height
                profileImageSize: 300, // ✅ GRÖSSERE IMAGE SIZE für Full-Height
                profileImageBorder: false, // ✅ KEIN FRAME
                profileImageBorderWidth: 0,
                profileImageShadow: false, // ✅ KEIN SHADOW
                contentSpacing: 0,    // ✅ KEIN CONTENT SPACING für Full-Height
                contentSpacingMobile: 20, // ✅ Mobile behält Spacing
                // ✅ FULL-HEIGHT-RIGHT SPEZIFISCHE CONTROLS
                textMaxWidth: 550,  // Angepasst für Full-Height Layout
                textMaxWidthMobile: 400,
                imageTextGap: 0,      // ✅ KEIN GAP für Full-Height - Image klebt am Rand
                imageTextGapMobile: 20, // Mobile hat Gap
                textVerticalAlign: 'center',
                desktopFlipSides: false, // ✅ KEIN FLIP - Layout-Direction ist bereits row-reverse
                mobileFlipSides: false,
                mobileStackOrder: 'image-first',
                elementSpacing: 25,
                elementSpacingMobile: 20,
                textLineHeight: 1.6,
                // ✅ FULL-HEIGHT IMAGE WIDTH DEFAULTS
                fullHeightImageWidth: 35,
                fullHeightImageWidthMobile: 40
            }
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ KRITISCH: BATCH UPDATE - Alle Werte auf einmal setzen
            const safePosition = widget.position !== undefined && widget.position !== null ? widget.position : 0;
            const updatedEditData = { ...editData, ...variations };
            
            // ✅ FIREBASE-SAFE: Position immer mitgesendet
            onUpdate({ ...updatedEditData, position: safePosition });
            
            console.log(`✅ HiWidgetEdit layout variations applied via batch update`);
        }
    }, [editData.layoutType, isInitialized, previousLayoutType, editData, onUpdate, widget.position]);

    // Text Input Configuration mit Font-Selektoren (OHNE Button)
    const textConfig = [
        {
            fieldName: 'greetingText',
            label: 'H2 - Primary Heading',
            type: 'input',
            placeholder: 'Hi, I\'m Laura!',
            fontField: 'greetingFont',
            fontType: 'big', // Primary Font
            effectField: 'greetingEffect'
        },
        {
            fieldName: 'titleText',
            label: 'H4 - Secondary Heading',
            type: 'input',
            placeholder: 'Enter title text...',
            fontField: 'titleFont',
            fontType: 'small', // Secondary Font
            effectField: 'titleEffect'
        },
        // ✅ NEU: Highlight Words Feld hinzufügen
        {
            fieldName: 'titleHighlightWords',
            label: 'Title - Highlight Words',
            type: 'input',
            placeholder: 'podcaster, author (comma separated)',
            description: 'Enter words to highlight with different styling',
            fontField: 'titleHighlightFont',
            fontType: 'big', // ✅ Anderer Font-Pool als Title (big statt small)
            effectField: 'titleHighlightEffect'
        },
        {
            fieldName: 'descriptionText',
            label: 'P - Description Text',
            type: 'textarea',
            placeholder: 'Enter description text...',
            rows: 4,
            fontField: 'descriptionFont',
            fontType: 'small', // Secondary Font
            effectField: 'descriptionEffect'
        }
    ];

    // Color Picker Configuration mit Button-Colors
    const colorConfig = [
        [
            {
                fieldName: 'greetingColor',
                label: 'Greeting',
                defaultValue: '#5C80BC',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'titleColor',
                label: 'Title',
                defaultValue: '#30323D',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'descriptionColor',
                label: 'Description',
                defaultValue: '#666666',
                icon: '/img/text.svg'
            }
        ],
        [
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#FFF8DC',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'profileImageBorderColor',
                label: 'Image Border',
                defaultValue: '#FFFFFF',
                icon: '/img/border.svg'
            }
        ]
    ];

    // Font Settings Configuration (OHNE Button-Fonts)
    const fontConfig = [
        {
            label: 'H2 - Primary',
            sizeField: 'greetingSize',
            weightField: 'greetingWeight',
            defaultSize: '48',
            defaultWeight: '700'
        },
        {
            label: 'H4 - Secondary',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            defaultSize: '24',
            defaultWeight: '600'
        },
        // ✅ NEU: Highlight Font Settings hinzufügen
        {
            label: 'Title - Highlight Words',
            sizeField: 'titleHighlightSize',
            weightField: 'titleHighlightWeight',
            defaultSize: '24', // Gleich wie Title
            defaultWeight: '800'
        },
        {
            label: 'P - Description',
            sizeField: 'descriptionSize',
            weightField: 'descriptionWeight',
            defaultSize: '18',
            defaultWeight: '400'
        }
    ];

    // Layout Settings Configuration
    // 🚀 CUSTOM LAYOUT PROPERTIES - HiWidget-spezifische Layout Properties
    const customLayoutProperties = [
        {
            section: 'Profile Image Settings',
            icon: 'fa-user-circle',
            properties: [
                {
                    id: 'profileImageSize',
                    label: 'Profile Image Size',
                    min: 100,
                    max: 400,
                    step: 10,
                    unit: 'px',
                    icon: '🖼️',
                    description: 'Size of the profile image',
                    defaultValue: 250,
                    responsive: true // ✅ Unterstützt Mobile/Desktop
                },
                {
                    id: 'profileImageBorderWidth',
                    label: 'Image Border Width',
                    min: 0,
                    max: 20,
                    step: 1,
                    unit: 'px',
                    icon: '🔲',
                    description: 'Width of the image border',
                    defaultValue: 4
                },
                {
                    id: 'imageOpacity',
                    label: 'Image Opacity',
                    min: 10,
                    max: 100,
                    step: 5,
                    unit: '%',
                    icon: '🌫️',
                    description: 'Opacity of the profile image',
                    defaultValue: 100
                },
                {
                    id: 'fullHeightImageWidth',
                    label: 'Full-Height Image Width',
                    min: 20,
                    max: 60,
                    step: 5,
                    unit: '%',
                    icon: '📐',
                    description: 'Width of image container in full-height layouts (% of total widget width)',
                    defaultValue: 35,
                    responsive: true // ✅ Unterstützt Mobile/Desktop
                },
                {
                    id: 'imageMarginToEdge',
                    label: 'Image Margin to Edge (Desktop)',
                    min: 0,
                    max: 80,
                    step: 5,
                    unit: 'px',
                    icon: '📏',
                    description: 'Margin between image and widget edge in side-by-side layouts (Desktop only)',
                    defaultValue: 40,
                    responsive: false // ✅ Nur Desktop
                }
            ]
        },
        {
            section: 'Content Spacing',
            icon: 'fa-arrows-h',
            properties: [
                {
                    id: 'contentSpacing',
                    label: 'Content Spacing',
                    min: 0,
                    max: 100,
                    step: 5,
                    unit: 'px',
                    icon: '📏',
                    description: 'Inner spacing around content (prevents content from touching edges)',
                    defaultValue: 40,
                    responsive: true // ✅ Unterstützt Mobile/Desktop
                }
            ]
        },
        {
            section: 'Layout Controls',
            icon: 'fa-arrows-alt',
            properties: [
                {
                    id: 'textMaxWidth',
                    label: 'Text Width',
                    min: 200,
                    max: 800,
                    step: 20,
                    unit: 'px',
                    icon: '📝',
                    description: 'Width of the text content area',
                    defaultValue: 600,
                    responsive: true // ✅ Unterstützt Mobile/Desktop
                },
                {
                    id: 'imageTextGap',
                    label: 'Image-Text Gap',
                    min: 10,
                    max: 100,
                    step: 5,
                    unit: 'px',
                    icon: '↔️',
                    description: 'Space between image and text content',
                    defaultValue: 40,
                    responsive: true // ✅ Unterstützt Mobile/Desktop
                },
            ]
        },
        {
            section: 'Flip & Order Controls',
            icon: 'fa-exchange-alt',
            properties: [
                {
                    id: 'desktopFlipSides',
                    label: 'Flip Sides (Desktop)',
                    type: 'toggle',
                    defaultValue: false,
                    icon: '🔄',
                    description: 'Switch image and text positions on desktop'
                },
                {
                    id: 'mobileFlipSides',
                    label: 'Flip Sides (Mobile)',
                    type: 'toggle',
                    defaultValue: false,
                    icon: '📱',
                    description: 'Switch image and text order on mobile (top/bottom)'
                },
            ]
        },
        {
            section: 'Advanced Spacing',
            icon: 'fa-expand-arrows-alt',
            properties: [
                {
                    id: 'elementSpacing',
                    label: 'Element Spacing',
                    min: 5,
                    max: 60,
                    step: 5,
                    unit: 'px',
                    icon: '⬇️',
                    description: 'Vertical space between text elements (greeting, title, description, button)',
                    defaultValue: 20,
                    responsive: true // ✅ Unterstützt Mobile/Desktop
                }
            ]
        }
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - Widget-spezifische Properties
    const advancedConfig = [
        // ✅ LAYOUT DROPDOWN CONTROLS
        {
            fieldName: 'textVerticalAlign',
            label: 'Text Vertical Alignment',
            type: 'select',
            options: [
                { value: 'flex-start', label: 'Top' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Bottom' }
            ],
            defaultValue: 'center',
            icon: '/img/element.svg',
            description: 'Vertical alignment of text relative to image'
        },
        {
            fieldName: 'mobileStackOrder',
            label: 'Mobile Stack Order',
            type: 'select',
            options: [
                { value: 'image-first', label: 'Image First' },
                { value: 'text-first', label: 'Text First' }
            ],
            defaultValue: 'image-first',
            icon: '/img/element.svg',
            description: 'Order of elements when stacked on mobile'
        },
        // ✅ NEU: Margin Controls für präzise Positionierung
        {
            fieldName: 'titleHighlightMarginLeft',
            label: 'Highlight Margin Left',
            type: 'slider',
            defaultValue: 0,
            min: -50,        // ✅ Erweitert: -50px für Überlappungen
            max: 50,         // ✅ Erweitert: +50px für große Abstände
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'titleHighlightMarginRight',
            label: 'Highlight Margin Right',
            type: 'slider',
            defaultValue: 0,
            min: -50,
            max: 50,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
                
                {/* ✅ LAYOUT SELECTOR */}
                <LayoutSelector
                    widgetType="hiWidget"
                    selectedLayout={editData.layoutType || 'classic-profile'}
                    onLayoutChange={handleLayoutChange}
                />
                
                {/* 🚀 PROFILE IMAGE CONTAINER - mit Frame Styles integriert */}
                <ImageContainer
                    label="Profile Image"
                    fieldName="profileImage"
                    currentImage={editData.profileImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingProfile}
                    setIsUploading={setIsUploadingProfile}
                    controls={!['full-height-left', 'full-height-right', 'image-top-full'].includes(editData.layoutType) ? [
                        {
                            type: 'image-shape',
                            label: 'Image Shape',
                            fieldName: 'profileImageShape',
                            defaultValue: 'circle'
                        },
                        {
                            type: 'size',
                            label: 'Image Size',
                            fieldName: 'profileImageSize',
                            defaultValue: 200,
                            min: 100,
                            max: 500,
                            step: 10,
                            unit: 'px',
                            description: 'Size of your profile image'
                        },
                        {
                            type: 'size',
                            label: 'Desktop Spacing',
                            fieldName: 'desktopSpacing',
                            defaultValue: 40,
                            min: 20,
                            max: 120,
                            step: 5,
                            unit: 'px',
                            description: 'Gap between image and text on desktop'
                        },
                        {
                            type: 'size',
                            label: 'Max Width',
                            fieldName: 'maxWidth',
                            defaultValue: 1200,
                            min: 600,
                            max: 1600,
                            step: 50,
                            unit: 'px',
                            description: 'Maximum width on large screens'
                        },
                        {
                            type: 'toggle',
                            label: 'Image Border',
                            fieldName: 'profileImageBorder'
                        },
                        {
                            type: 'toggle',
                            label: 'Image Shadow',
                            fieldName: 'profileImageShadow'
                        },
                        {
                            type: 'size',
                            label: 'Border Width',
                            fieldName: 'profileImageBorderWidth',
                            defaultValue: 4,
                            min: 1,
                            max: 20,
                            step: 1,
                            unit: 'px',
                            description: 'Thickness of the image border',
                            showWhen: 'profileImageBorder' // Nur anzeigen wenn Border aktiv
                        }
                    ] : []}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* Text Inputs mit Font-Selektoren und Effects */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                />

                {/* 🚀 REVOLUTIONARY BUTTON STYLE SECTION */}
                <ButtonWidgetStyle 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    buttonConfig={{
                        textField: 'buttonText',
                        linkField: 'buttonLink'
                    }}
                />

                {/* More Settings mit Toggle (inkl. Colors) */}
                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard Layout Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* 🔧 ADVANCED SETTINGS - Widget-spezifische Properties */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Empty - moved to Revolutionary Layout Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>
        </div>
    );
}

export default HiWidgetEdit;
